import { useMemo } from 'react';
import * as d3 from 'd3';
import { MarketAnalysisData, MarketMetadata, VolumeVisibility, AggregatedVolumePoint } from '../../types';
import { ChartLayout, ParsedChartPoint, ChartScales } from '../types';
import { createChartScales } from '../utils/createScales';

interface UseChartScalesParams {
    parsedData: ParsedChartPoint[];
    analysisData: MarketAnalysisData | null | undefined;
    markets: MarketMetadata[];
    layout: ChartLayout;
    isFullSeriesMode: boolean;
    isLogScale: boolean;
    useDecimalScale: boolean;
    endDate?: Date;
    aggregatedVolume: AggregatedVolumePoint[];
    volumeVisibility: VolumeVisibility;
}

interface ChartScalesResult extends ChartScales {
    timeDomain: [Date, Date];
}

export function useChartScales({
    parsedData,
    analysisData,
    markets,
    layout,
    isFullSeriesMode,
    isLogScale,
    useDecimalScale,
    endDate,
    aggregatedVolume,
    volumeVisibility,
}: UseChartScalesParams): ChartScalesResult | null {
    return useMemo(() => {
        // Check if we have data to create scales
        const hasSimpleData = parsedData.length > 0 && markets.length > 0;
        const hasFullSeriesData = isFullSeriesMode && analysisData && (
            analysisData.bidAsk.length > 0 ||
            analysisData.fairValue.length > 0 ||
            analysisData.trades.length > 0 ||
            (analysisData.candles?.length ?? 0) > 0
        );

        if (!hasSimpleData && !hasFullSeriesData) {
            return null;
        }

        return createChartScales({
            parsedData,
            analysisData: analysisData || null,
            markets,
            layout,
            isFullSeriesMode,
            isLogScale,
            useDecimalScale,
            endDate,
            aggregatedVolume,
            volumeMetric: volumeVisibility.metric,
        });
    }, [
        parsedData,
        analysisData,
        markets,
        layout,
        isFullSeriesMode,
        isLogScale,
        useDecimalScale,
        endDate,
        aggregatedVolume,
        volumeVisibility.metric,
    ]);
}

export function useParsedData(
    data: { timestamp: string; [key: string]: any }[]
): ParsedChartPoint[] {
    return useMemo(() => {
        const parseTime = d3.timeParse('%Y-%m-%dT%H:%M:%S.%LZ');
        return data.map(d => ({
            ...d,
            parsedTimestamp: parseTime(d.timestamp) || new Date(d.timestamp)
        }));
    }, [data]);
}
